'use client'

import { useEffect, useRef, useCallback } from 'react'
import { recordAnalyticsEventsBatch, AnalyticsEvent } from '@/lib/analytics/actions'

type ViewportTrackingOptions = {
  userId: string
  itemId: string
  itemType: 'product' | 'solution'
  threshold?: number // Percentage of element that must be visible (0-1)
  minVisibilityTime?: number // Minimum time in ms that element must be visible
  enabled?: boolean // Whether tracking is enabled
}

/**
 * Hook to track when a card/element enters the viewport and record an analytics event
 * Implements batching to reduce server load - events are sent every 5 seconds
 */
export function useViewportTracking(options: ViewportTrackingOptions) {
  const {
    userId,
    itemId,
    itemType,
    threshold = 0.5, // 50% of element must be visible
    minVisibilityTime = 1000, // Element must be visible for 1 second
    enabled = true,
  } = options

  const elementRef = useRef<HTMLDivElement>(null)
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedRef = useRef(false)

  const trackView = useCallback(() => {
    if (hasTrackedRef.current || !enabled) return

    // Add to batch queue instead of sending immediately
    addToBatch({
      userId,
      itemId,
      itemType,
      viewedAt: new Date(),
    })

    hasTrackedRef.current = true
  }, [userId, itemId, itemType, enabled])

  useEffect(() => {
    if (!enabled || !elementRef.current) return

    const element = elementRef.current

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Element entered viewport
            // Wait for minimum visibility time before tracking
            visibilityTimerRef.current = setTimeout(() => {
              trackView()
            }, minVisibilityTime)
          } else {
            // Element left viewport - cancel the timer if it's still running
            if (visibilityTimerRef.current) {
              clearTimeout(visibilityTimerRef.current)
              visibilityTimerRef.current = null
            }
          }
        })
      },
      {
        threshold,
        rootMargin: '0px',
      },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current)
      }
    }
  }, [enabled, threshold, minVisibilityTime, trackView])

  return elementRef
}

// Batch queue management - shared across all useViewportTracking instances
let eventBatch: AnalyticsEvent[] = []
let batchTimer: NodeJS.Timeout | null = null
const BATCH_INTERVAL = 5000 // Send batch every 5 seconds
const MAX_BATCH_SIZE = 50 // Send batch if it reaches 50 events

function addToBatch(event: AnalyticsEvent) {
  eventBatch.push(event)

  // If batch is full, send immediately
  if (eventBatch.length >= MAX_BATCH_SIZE) {
    sendBatch()
    return
  }

  // Otherwise, schedule a batch send if not already scheduled
  if (!batchTimer) {
    batchTimer = setTimeout(() => {
      sendBatch()
    }, BATCH_INTERVAL)
  }
}

async function sendBatch() {
  if (eventBatch.length === 0) return

  const eventsToSend = [...eventBatch]
  eventBatch = []

  // Clear the timer
  if (batchTimer) {
    clearTimeout(batchTimer)
    batchTimer = null
  }

  try {
    const result = await recordAnalyticsEventsBatch(eventsToSend)
    if (!result.success) {
      console.warn('Failed to record some analytics events')
    }
  } catch (error) {
    console.error('Failed to send analytics batch:', error)
  }
}

// Send remaining events when page is about to unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventBatch.length > 0) {
      sendBatch()
    }
  })

  // Also send on visibility change (when user switches tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && eventBatch.length > 0) {
      sendBatch()
    }
  })
}
